import { Component, inject, OnInit, Input, OnChanges, Output, EventEmitter } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule, DatePipe } from '@angular/common';
import { CdkDragDrop, CdkDrag, CdkDropList, CdkDragHandle, moveItemInArray } from '@angular/cdk/drag-drop';
import { TaskService } from '../../../../shared/services/task.service';
import { User } from '../../../../shared/services/user';
import { Task } from '../../../../shared/models/task.model';
import { ProjectLink } from '../../../../shared/models/project.model';
import { ExternalLinkService } from '../../../../shared/services/external-link.service';
import { User as UserModel } from '../../../../shared/models/user.model';
import { UserService } from '../../../../shared/services/user.service';

@Component({
  selector: 'app-list-task',
  imports: [FormsModule, CommonModule, CdkDrag, CdkDropList, CdkDragHandle],
  providers: [DatePipe],
  templateUrl: './list-task.html',
  styleUrls: ['./list-task.scss']
})
export class ListTaskComponent implements OnInit, OnChanges {
  @Input() projectStageId: string | null = null;
  @Input() projectManagerUserIds: string[] = []; // User IDs of project managers
  @Input() filteredTasks: Task[] | null = null; // Pre-filtered tasks from parent (status filter)
  @Output() taskDetailOpen = new EventEmitter<string>();

  private taskService = inject(TaskService);
  private userAuthService = inject(User);  // For admin check
  private userService = inject(UserService);  // For users list
  private externalLinkService = inject(ExternalLinkService);
  private datePipe = inject(DatePipe);

  loading = false;

  /** Set when tasks could not be loaded at all — shown instead of the empty state. */
  loadError: string | null = null;

  private allTasks: Task[] = [];
  tasks: Task[] = [];

  employees: UserModel[] = [];
  currentUserId: string | null = null;

  async ngOnInit() {
    this.loading = true;
    this.taskService.tasks$.subscribe(tasks => {
      this.allTasks = tasks;
      this.filterTasks();
      this.loading = false;
    });
    this.taskService.tasksError$.subscribe(message => {
      this.loadError = message;
      // Only stop the spinner on a real failure; the initial null emission of the
      // BehaviorSubject must not pre-empt the first tasks$ emission.
      if (message) this.loading = false;
    });
    this.userService.users$.subscribe(employees => {
      this.employees = employees;
    });
    this.userService.currentUser$.subscribe(user => {
      this.currentUserId = user?.id || null;
    });
  }

  ngOnChanges() {
    this.filterTasks();
  }

  filterTasks() {
    const source = this.filteredTasks ?? this.allTasks;
    if (this.projectStageId) this.tasks = source.filter(t => t.projectStageId === this.projectStageId);
    else this.tasks = source;
  }

  /**
   * Every user may edit a task, but a completed one is frozen — reopening it
   * (the checkbox) is the only change it still accepts.
   */
  canUserEditTask(task: Task): boolean {
    return task.status !== 'DONE';
  }

  updateField(taskId: string, updates: Partial<Task>) {
    // Find the task
    const task = this.tasks.find(t => t.id === taskId);
    if (!task) return;


    if (updates.limitDateToDo) {
      const date = new Date(updates.limitDateToDo);
      if (!isNaN(date.getTime())) updates.limitDateToDo = date.toISOString();
      else delete updates.limitDateToDo;
    }
    if ('employeeId' in updates && !updates.employeeId) updates.employeeId = null;
    this.taskService.updateTask(taskId, updates)
      .then(() => console.log('Task updated'))
      .catch(err => console.error(err));
  }

  deleteTask(taskId: string) {
    this.taskService.deleteTask(taskId)
      .then(() => console.log('Task deleted'))
      .catch(err => console.error(err));
  }

  /** True when the link points at Google Drive, so the row can show the Drive icon. */
  isDriveLink(link: ProjectLink): boolean {
    return /drive\.google\.com|docs\.google\.com/.test(link?.url || '');
  }

  openExternalLink(url: string, event?: Event): void {
    event?.stopPropagation();
    if (url) this.externalLinkService.openExternal(url, event);
  }

  openTaskDetail(taskId: string) {
    // The parent navigates to the task page, whose URL is the deep link — no need
    // to also stamp a ?taskId= query param on the project route.
    this.taskDetailOpen.emit(taskId);
  }

  getTaskEmployees(task: Task): UserModel[] {
    if (!task.employeeId || !Array.isArray(task.employeeId)) return [];
    return this.employees.filter(emp => emp.id && task.employeeId?.includes(emp.id));
  }

  /** Toggle a task's done state via the row checkbox. */
  toggleDone(task: Task) {
    // Deliberately not gated by canUserEditTask: unchecking a DONE task is the reopen path.
    if (!task.id) return;
    const next = task.status === 'DONE' ? 'INPROGRESS' : 'DONE';
    task.status = next;
    this.updateField(task.id, { status: next });
  }

  /** Short assignee label: "Prénom N." + "+N" when several are assigned. */
  getAssigneeLabel(task: Task): string {
    const emps = this.getTaskEmployees(task);
    if (!emps.length) return '';
    const first = emps[0];
    const initial = (first.name || '').charAt(0).toUpperCase();
    const label = `${first.firstName || ''}${initial ? ' ' + initial + '.' : ''}`.trim();
    return emps.length > 1 ? `${label} +${emps.length - 1}` : label;
  }

  /** French priority badge info. */
  getPriorityInfo(priority: string | null | undefined): { label: string; classes: string } | null {
    switch (priority) {
      case 'HIGH': return { label: 'High', classes: 'bg-red-100 text-red-700' };
      case 'MEDIUM': return { label: 'Medium', classes: 'bg-amber-100 text-amber-700' };
      case 'LOW': return { label: 'Low', classes: 'bg-gray-100 text-gray-600' };
      default: return null;
    }
  }

  /** Due-date display: "Aujourd'hui" / formatted date, flagged urgent when today or overdue. */
  getDueInfo(task: Task): { text: string; urgent: boolean } | null {
    if (!task.limitDateToDo) return null;
    const due = new Date(task.limitDateToDo);
    if (isNaN(due.getTime())) return null;
    const today = new Date();
    const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const diffDays = Math.round((startOfDay(due) - startOfDay(today)) / 86400000);
    const done = task.status === 'DONE';
    if (diffDays === 0) return { text: 'Today', urgent: !done };
    if (diffDays < 0) return { text: this.datePipe.transform(due, 'd MMM') || '', urgent: !done };
    return { text: this.datePipe.transform(due, 'd MMM') || '', urgent: false };
  }

  getEmployeeNames(employees: UserModel[]): string {
    return employees.map(e => e.firstName + ' ' + e.name).join(', ');
  }

  // Check if user is admin
  isAdmin(): boolean {
    return this.userAuthService.admin();
  }

  // Drag & Drop handler for tasks
  async onTaskDrop(event: CdkDragDrop<Task[]>) {
    if (event.previousIndex === event.currentIndex) return;

    const task = this.tasks[event.previousIndex];

    // Check if user has permission to reorder this task
    if (!this.canUserEditTask(task)) {
      console.warn('User does not have permission to reorder this task');
      return;
    }

    // Reorder locally
    moveItemInArray(this.tasks, event.previousIndex, event.currentIndex);

    // Filter tasks for the current stage only
    const tasksForStage = this.tasks.filter(t => t.projectStageId === this.projectStageId);

    // Update order in backend
    await this.taskService.reorderTasks(tasksForStage);
  }

  getValidatedByName(userId: string | null): string {
    if (!userId) return '';
    const emp = this.employees.find(e => e.id === userId);
    if (emp) return `${emp.firstName ?? ''} ${emp.name ?? ''}`.trim();
    return 'Unknown';
  }

  getInitials(emp: UserModel): string {
    const first = (emp.firstName || '').charAt(0).toUpperCase();
    const last = (emp.name || '').charAt(0).toUpperCase();
    return (first + last) || '?';
  }

  getValidatedByInitials(userId: string | null): string {
    if (!userId) return '?';
    const emp = this.employees.find(e => e.id === userId);
    if (!emp) return '?';
    return this.getInitials(emp);
  }

  formatRelativeTime(dateString: string | null): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    const diffH = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMin < 1) return "à l'instant";
    if (diffMin < 60) return `il y a ${diffMin}min`;
    if (diffH < 24) return `il y a ${diffH}h`;
    if (diffDays === 1) return 'hier';
    if (diffDays < 7) return `il y a ${diffDays}j`;
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  }
}
